// API service for communicating with Flask backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://animesh-flask-api-2024-c8ckc6ctekb2byg0.canadacentral-01.azurewebsites.net';
// const API_BASE_URL = 'http://localhost:5001';

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  code?: string;
  message?: string;
}

export interface SignupRequest {
  email: string;
  name: string;
  organisation: string;
}

export interface SignupResponse {
  message: string;
  next: string;
  user_id: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  redirect_url: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  user_id: string;
}

export interface UserProfile {
  displayName: string;
  mail: string;
  userPrincipalName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  officeLocation?: string;
}

export interface PasswordUpdateRequest {
  currentPassword: string;
  newPassword: string;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      
      // Get Azure AD token if available
      const azureToken = localStorage.getItem('azureAccessToken');
      
      const defaultOptions: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(azureToken && { 'Authorization': `Bearer ${azureToken}` }),
          ...options.headers,
        },
        ...options,
      };

      const response = await fetch(url, defaultOptions);
      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'An error occurred',
          code: data.code || 'UNKNOWN_ERROR',
        };
      }

      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    }
  }

  async signup(credentials: SignupRequest): Promise<ApiResponse<SignupResponse>> {
    return this.makeRequest<SignupResponse>('/api/signup', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.makeRequest<LoginResponse>('/api/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async healthCheck(): Promise<ApiResponse<{ status: string; service: string }>> {
    return this.makeRequest<{ status: string; service: string }>('/health');
  }

  // Microsoft Graph API methods
  async getUserProfile(accessToken: string): Promise<ApiResponse<UserProfile>> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return {
          error: `Failed to fetch user profile: ${response.statusText}`,
          code: 'GRAPH_API_ERROR',
        };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Graph API request failed:', error);
      return {
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    }
  }

  async updateUserPassword(accessToken: string, passwordData: PasswordUpdateRequest): Promise<ApiResponse<any>> {
    try {
      // Note: Microsoft Graph API doesn't support password updates directly
      // This would typically require Azure AD admin privileges or a different approach
      // For now, we'll return an error explaining the limitation
      
      return {
        error: 'ERROR: Please contact your administrator',
        code: 'PASSWORD_UPDATE_NOT_SUPPORTED',
      };
    } catch (error) {
      console.error('Password update request failed:', error);
      return {
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    }
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for testing purposes
export default ApiService;
